import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Card, Button, Form, Alert, Badge, ButtonGroup, Spinner } from "react-bootstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { scanImageData, setModuleArgs } from "@undecaf/zbar-wasm";
import { fetchMapperData, replaceCatalog, upsertMapping, clearAllMappings, deleteItem as deleteItemApi, fetchLatestSalesByPackUpc } from "../services/upcMapping.service";
import SwipeableCard from "./SwipeableCard.component";

// zbar-wasm fetches its WASM binary at runtime. The binary lives in
// public/zbar.wasm so CRA's dev server serves it at /zbar.wasm.
setModuleArgs({ locateFile: () => `${process.env.PUBLIC_URL || ""}/zbar.wasm` });

// Cigarette cartons/packs sometimes carry a tiny FSC / track-and-trace
// DataMatrix instead of a standard 1D barcode. Default zxing tuning misses
// these — enable every common format + TRY_HARDER, and request a high-res
// stream so small 2D codes have enough pixels to decode.
const SCAN_HINTS = new Map();
SCAN_HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.EAN_8,
  BarcodeFormat.EAN_13,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.CODE_128,
  BarcodeFormat.ITF,
  BarcodeFormat.CODABAR,
  BarcodeFormat.QR_CODE,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.AZTEC,
  BarcodeFormat.PDF_417,
]);
SCAN_HINTS.set(DecodeHintType.TRY_HARDER, true);

const VIDEO_CONSTRAINTS = {
  video: {
    facingMode: { ideal: "environment" },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
  audio: false,
};

const STORAGE_KEY = "shoolin.cigaretteMapper.v1";
const MAPPING_TYPE = "cigarette";

function loadState() {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (v && Array.isArray(v.items)) return v;
  } catch {}
  return { fileName: "", importedAt: 0, items: [] };
}
function saveState(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
}

function rowToItem(row, existingByUpc) {
  const upc = String(row["UPC"] ?? "").trim();
  const existing = existingByUpc.get(upc);
  return {
    upc,
    itemNumber: String(row["Item #"] ?? "").trim(),
    description: String(row["Description"] ?? "").trim(),
    latestPrice: Number(row["Latest Price"] ?? 0) || 0,
    timesOrdered: Number(row["Times Ordered"] ?? 0) || 0,
    totalQty: Number(row["Total Qty"] ?? 0) || 0,
    packUpc: existing?.packUpc || "",
    scannedAt: existing?.scannedAt || 0,
  };
}

// Crop the central square region of the video frame matching the visual
// framing rectangle (~60% of the smaller dim). Cropping before decode is
// effectively a digital zoom — gives the decoder ~2× more pixels per bar of
// a small EAN-8 vs. decoding the full frame.
function cropToFramingRect(video) {
  const canvas = document.createElement("canvas");
  const minDim = Math.min(video.videoWidth, video.videoHeight);
  const size = Math.max(64, Math.round(minDim * 0.6));
  const sx = Math.round((video.videoWidth - size) / 2);
  const sy = Math.round((video.videoHeight - size) / 2);
  canvas.width = size;
  canvas.height = size;
  canvas.getContext("2d").drawImage(video, sx, sy, size, size, 0, 0, size, size);
  return canvas;
}

// Native browser BarcodeDetector. iOS Safari 16+, Chrome 83+. The same
// decoder behind the iPhone Camera "scan barcode" feature — significantly
// better than zxing-js on small / low-contrast EAN-8 codes. Returns the
// decoded text or null.
const NATIVE_FORMATS = ["ean_8", "ean_13", "upc_a", "upc_e", "code_128", "code_39", "code_93", "codabar", "itf", "qr_code", "data_matrix", "aztec", "pdf417"];
async function decodeViaNative(canvas) {
  if (typeof window === "undefined" || !("BarcodeDetector" in window)) return null;
  try {
    const supported = (await window.BarcodeDetector.getSupportedFormats?.()) || [];
    const formats = NATIVE_FORMATS.filter((f) => supported.length === 0 || supported.includes(f));
    if (!formats.length) return null;
    const detector = new window.BarcodeDetector({ formats });
    const codes = await detector.detect(canvas);
    return codes.length ? codes[0].rawValue : null;
  } catch {
    return null;
  }
}

async function decodeViaZxing(canvas) {
  const dataUrl = canvas.toDataURL("image/png");
  const img = new Image();
  img.src = dataUrl;
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
  const reader = new BrowserMultiFormatReader(SCAN_HINTS);
  const result = await reader.decodeFromImageElement(img);
  return result.getText();
}

// zbar-wasm — WASM port of the C zbar library. Significantly stronger than
// zxing-js on small/blurry/low-contrast 1D codes and tiny DataMatrix
// (cigarette track-and-trace). Returns the decoded text or null.
async function decodeViaZbar(canvas) {
  const ctx = canvas.getContext("2d");
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const symbols = await scanImageData(imageData);
  if (!symbols || !symbols.length) return null;
  return symbols[0].decode();
}

// Try to bump optical zoom on the video track if the device supports it.
// Most iPhones (≥iOS 17) and modern Androids do; older devices ignore.
// Returns a short report string for the diagnostic overlay.
async function tryApplyZoomReport(stream, factor = 2) {
  try {
    const track = stream?.getVideoTracks?.()[0];
    if (!track) return "no track";
    const caps = track.getCapabilities?.() || {};
    let report = [];
    if (caps.zoom) {
      const target = Math.min(caps.zoom.max || factor, Math.max(caps.zoom.min || 1, factor));
      await track.applyConstraints({ advanced: [{ zoom: target }] });
      report.push(`zoom ${target}×`);
    } else {
      report.push("no zoom cap");
    }
    if (caps.focusMode && caps.focusMode.includes("continuous")) {
      await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
      report.push("continuous focus");
    } else {
      report.push("no focus cap");
    }
    return report.join(" · ");
  } catch (e) {
    return "error: " + (e?.message || "unknown");
  }
}

function formatLastSold(latest) {
  if (!latest) return null;
  const raw = latest.entered || latest.date;
  let when = "";
  if (raw) {
    const d = new Date(raw);
    when = isNaN(d.getTime()) ? String(raw) : d.toLocaleDateString();
  }
  const bits = [];
  if (when) bits.push(when);
  if (latest.item) bits.push(String(latest.item));
  if (latest.qty != null) bits.push(`qty ${latest.qty}`);
  if (latest.amount != null) bits.push(`$${Number(latest.amount).toFixed(2)}`);
  return bits.join(" · ");
}

function ItemCard({ item, latestSale, isScanning, onStartScan, onStopScan, onScanned, onClear, onManualEdit, onDelete }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const streamRef = useRef(null);
  const lastResultRef = useRef({ text: "", at: 0 });
  const [scanError, setScanError] = useState("");
  const [snapStatus, setSnapStatus] = useState(""); // transient feedback for snap-decode
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.packUpc);
  // Visible diagnostics so we can see live what the decoders are actually
  // doing on the user's device. Helps when "snap doesn't work" is the only
  // thing we know.
  const [diag, setDiag] = useState({
    nativeAvailable: typeof window !== "undefined" && "BarcodeDetector" in window,
    nativeFormats: [],
    eanSupported: null,
    videoSize: "",
    zoomApplied: "",
    snapAttempts: 0,
    lastNative: "",
    lastZbar: "",
    lastZxing: "",
  });

  // Single-shot decode: capture the current video frame, crop to the framing
  // box, try native BarcodeDetector first, fall back to zxing. Far more
  // reliable for tiny EAN-8s than continuous video frames.
  const snapAndDecode = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setSnapStatus("Camera not ready yet.");
      return;
    }
    setSnapStatus("Decoding…");
    setScanError("");
    setDiag((d) => ({ ...d, snapAttempts: d.snapAttempts + 1, lastNative: "trying…", lastZbar: "", lastZxing: "" }));
    try {
      const canvas = cropToFramingRect(video);
      let nativeCode = null;
      let nativeErr = "";
      try { nativeCode = await decodeViaNative(canvas); } catch (e) { nativeErr = e?.message || "native error"; }
      let zbarCode = null;
      let zbarErr = "";
      if (!nativeCode) {
        try { zbarCode = await decodeViaZbar(canvas); } catch (e) { zbarErr = e?.message || "zbar error"; }
      }
      let zxingCode = null;
      let zxingErr = "";
      if (!nativeCode && !zbarCode) {
        try { zxingCode = await decodeViaZxing(canvas); } catch (e) { zxingErr = e?.message || "no match"; }
      }
      let code = nativeCode || zbarCode || zxingCode;
      if (!code) {
        // Try once more on the un-cropped full frame in case the user
        // missed the framing box entirely.
        const full = document.createElement("canvas");
        full.width = video.videoWidth;
        full.height = video.videoHeight;
        full.getContext("2d").drawImage(video, 0, 0, full.width, full.height);
        try { code = await decodeViaNative(full); } catch {}
        if (!code) {
          try { code = await decodeViaZbar(full); } catch {}
        }
        if (!code) {
          try { code = await decodeViaZxing(full); } catch {}
        }
      }
      setDiag((d) => ({
        ...d,
        lastNative: nativeCode ? `✓ ${nativeCode}` : (nativeErr || "(no match)"),
        lastZbar: zbarCode ? `✓ ${zbarCode}` : (zbarErr || (nativeCode ? "—" : "(no match)")),
        lastZxing: zxingCode ? `✓ ${zxingCode}` : (zxingErr || (nativeCode || zbarCode ? "—" : "(no match)")),
      }));
      if (code) {
        if (navigator.vibrate) navigator.vibrate(60);
        setSnapStatus("");
        onScanned(code);
      } else {
        setSnapStatus("No code found — fill the box with the barcode, tap screen to focus, hold steady.");
      }
    } catch (e) {
      setSnapStatus("Decode error: " + (e?.message || "unknown"));
    }
  };

  useEffect(() => {
    if (!isScanning) return;
    let cancelled = false;
    let nativePollHandle = null;
    setScanError("");
    const reader = new BrowserMultiFormatReader(SCAN_HINTS);

    (async () => {
      try {
        const controls = await reader.decodeFromConstraints(
          VIDEO_CONSTRAINTS,
          videoRef.current,
          (result) => {
            if (cancelled || !result) return;
            const text = result.getText();
            const now = Date.now();
            if (text === lastResultRef.current.text && now - lastResultRef.current.at < 1500) return;
            lastResultRef.current = { text, at: now };
            if (navigator.vibrate) navigator.vibrate(40);
            onScanned(text);
          }
        );
        if (cancelled) { controls.stop(); return; }
        controlsRef.current = controls;

        // Capture the active stream + try to apply 2× zoom and continuous focus
        const stream = videoRef.current?.srcObject;
        streamRef.current = stream;
        const zoomReport = await tryApplyZoomReport(stream, 2);

        // Populate diagnostics for the visible overlay
        const v = videoRef.current;
        const trackSettings = stream?.getVideoTracks?.()[0]?.getSettings?.() || {};
        let nativeFormats = [];
        let eanSupported = null;
        if ("BarcodeDetector" in window) {
          try {
            nativeFormats = (await window.BarcodeDetector.getSupportedFormats?.()) || [];
            eanSupported = nativeFormats.includes("ean_8");
          } catch {}
        }
        setDiag((d) => ({
          ...d,
          nativeAvailable: "BarcodeDetector" in window,
          nativeFormats,
          eanSupported,
          videoSize: v?.videoWidth ? `${v.videoWidth}×${v.videoHeight}` : (trackSettings.width ? `${trackSettings.width}×${trackSettings.height}` : "?"),
          zoomApplied: zoomReport,
        }));

        // Run native BarcodeDetector + zbar in parallel at ~4fps on the
        // cropped framing box. Both are faster + more accurate for small
        // codes than zxing's continuous video pipeline.
        const tick = async () => {
          if (cancelled) return;
          const v = videoRef.current;
          if (v && v.videoWidth) {
            try {
              const canvas = cropToFramingRect(v);
              let code = null;
              if ("BarcodeDetector" in window) {
                try { code = await decodeViaNative(canvas); } catch {}
              }
              if (!code) {
                try { code = await decodeViaZbar(canvas); } catch {}
              }
              if (code) {
                const now = Date.now();
                if (code !== lastResultRef.current.text || now - lastResultRef.current.at >= 1500) {
                  lastResultRef.current = { text: code, at: now };
                  if (navigator.vibrate) navigator.vibrate(40);
                  onScanned(code);
                  return;
                }
              }
            } catch {}
          }
          if (!cancelled) nativePollHandle = setTimeout(tick, 250);
        };
        nativePollHandle = setTimeout(tick, 300);
      } catch (e) {
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError") {
          setScanError("Camera permission denied.");
        } else if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
          setScanError("Camera requires HTTPS.");
        } else {
          setScanError(e?.message || "Could not start camera.");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (nativePollHandle) clearTimeout(nativePollHandle);
      try { controlsRef.current?.stop(); } catch {}
      controlsRef.current = null;
      streamRef.current = null;
    };
  }, [isScanning, onScanned]);

  // Tap-to-focus: on supported devices, set a focus point of interest
  // where the user tapped the video element.
  const handleVideoTap = async (e) => {
    const stream = streamRef.current;
    if (!stream) return;
    const track = stream.getVideoTracks()[0];
    const caps = track?.getCapabilities?.() || {};
    if (!caps.focusMode || !caps.focusMode.includes("manual")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    try {
      await track.applyConstraints({
        advanced: [{ focusMode: "manual", pointsOfInterest: [{ x, y }] }],
      });
      // Restore continuous focus shortly after — keeps frame sharp on movement
      setTimeout(() => {
        track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
      }, 1500);
    } catch {}
  };

  const isScanned = !!item.packUpc;

  const saveEdit = () => {
    onManualEdit(draft.trim());
    setEditing(false);
  };

  return (
    <Card className={`mb-3 ${isScanned ? "border-success" : ""}`}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h5 className="mb-1" style={{ wordBreak: "break-word" }}>{item.description || "(no description)"}</h5>
            <div className="text-muted small">
              ${item.latestPrice.toFixed(2)} · ordered {item.timesOrdered}× · qty {item.totalQty}
              {item.itemNumber && ` · Item# ${item.itemNumber}`}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 ms-2">
            <Badge bg={isScanned ? "success" : "secondary"}>
              {isScanned ? "Scanned" : "Unscanned"}
            </Badge>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-danger"
              style={{ fontSize: 18, lineHeight: 1, textDecoration: "none" }}
              onClick={() => {
                if (window.confirm(`Delete "${item.description || item.upc}" from the mapper?`)) {
                  onDelete?.();
                }
              }}
              aria-label="Delete this item"
              title="Delete this item"
            >
              ×
            </Button>
          </div>
        </div>

        <div className="row g-2 mb-2">
          <div className="col-12 col-md-6">
            <div className="text-muted small">Carton UPC</div>
            <div style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
              {item.upc || <span className="text-muted">—</span>}
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="text-muted small">Pack UPC</div>
            {editing ? (
              <div className="d-flex gap-2">
                <Form.Control
                  size="sm"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Enter pack UPC"
                  autoFocus
                />
                <Button size="sm" variant="primary" onClick={saveEdit}>Save</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => { setEditing(false); setDraft(item.packUpc); }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                {item.packUpc || <span className="text-muted">Not scanned</span>}
              </div>
            )}
            {item.packUpc && !editing && (
              <div className="text-muted small mt-1">
                {latestSale === undefined ? (
                  <span>Looking up last sale…</span>
                ) : latestSale === null ? (
                  <span>No sales found for this pack UPC.</span>
                ) : (
                  <span>Last sold: {formatLastSold(latestSale)}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {isScanning && (
          <div
            style={{
              position: "relative",
              background: "#000",
              borderRadius: 8,
              overflow: "hidden",
              aspectRatio: "4 / 3",
              marginTop: 8,
            }}
          >
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              onClick={handleVideoTap}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", cursor: "pointer" }}
            />
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "min(55%, 220px)",
                aspectRatio: "1 / 1",
                border: "2px solid rgba(255,255,255,0.95)",
                borderRadius: 8,
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
                pointerEvents: "none",
              }}
            />
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 8, textAlign: "center", color: "#fff", fontSize: 13, textShadow: "0 1px 2px rgba(0,0,0,0.7)" }}>
              Fill the box with the code · hold steady · tap screen to focus
            </div>
          </div>
        )}

        {scanError && (
          <Alert variant="danger" className="mt-2 mb-0 py-2 small">{scanError}</Alert>
        )}
        {isScanning && snapStatus && (
          <div className="mt-2 small text-muted">{snapStatus}</div>
        )}
        {isScanning && (
          <div className="mt-2 p-2 small" style={{ background: "#f6f8fa", borderRadius: 6, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11, lineHeight: 1.5 }}>
            <div>Native BarcodeDetector: <strong>{diag.nativeAvailable ? "yes" : "no"}</strong>{diag.eanSupported === false && <span className="text-danger"> · ean_8 NOT supported by this Safari</span>}{diag.eanSupported === true && <span className="text-success"> · ean_8 ✓</span>}</div>
            {diag.nativeFormats.length > 0 && <div>Formats: {diag.nativeFormats.join(", ")}</div>}
            <div>Video: {diag.videoSize || "starting…"} · {diag.zoomApplied || "—"}</div>
            <div>Snap attempts: {diag.snapAttempts}{diag.snapAttempts > 0 && <> · native: {diag.lastNative} · zbar: {diag.lastZbar} · zxing: {diag.lastZxing}</>}</div>
          </div>
        )}

        <div className="d-flex flex-wrap gap-2 mt-3">
          {isScanning ? (
            <>
              <Button variant="primary" size="sm" onClick={snapAndDecode}>
                📸 Snap to scan
              </Button>
              <Button variant="outline-danger" size="sm" onClick={onStopScan}>Stop camera</Button>
            </>
          ) : (
            <Button variant={isScanned ? "outline-primary" : "primary"} size="sm" onClick={onStartScan}>
              📷 {isScanned ? "Rescan" : "Scan pack UPC"}
            </Button>
          )}
          {!editing && (
            <Button variant="outline-secondary" size="sm" onClick={() => { setDraft(item.packUpc); setEditing(true); }}>
              {isScanned ? "Edit" : "Type manually"}
            </Button>
          )}
          {isScanned && !editing && (
            <Button variant="outline-secondary" size="sm" onClick={onClear}>Clear</Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

function CigaretteMapper() {
  const [state, setState] = useState(loadState);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [scanningUpc, setScanningUpc] = useState(null);
  const [importError, setImportError] = useState("");
  const [syncError, setSyncError] = useState("");
  const [syncing, setSyncing] = useState(true);

  // Bulk-scan mode: hardware barcode scanner (USB/Bluetooth) types digits +
  // Enter into a hidden, always-focused input. We save against the current
  // "active" SKU and auto-advance to the next unscanned one.
  const [bulkMode, setBulkMode] = useState(false);
  const [activeUpc, setActiveUpc] = useState(null);
  const [bulkBuffer, setBulkBuffer] = useState("");
  const [bulkLastSaved, setBulkLastSaved] = useState(null);
  const bulkInputRef = useRef(null);

  // Most recent SalesTransaction per pack UPC. Two sentinel states per key:
  //   undefined → not yet fetched (shows "Looking up…")
  //   null      → fetched, no sales found
  //   object    → { entered, qty, amount, ... }
  const [latestSales, setLatestSales] = useState({});

  const items = state.items;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { catalog, mappings } = await fetchMapperData(MAPPING_TYPE);
        if (cancelled) return;
        if (!catalog) return;
        const byId = new Map(mappings.map((m) => [m.externalId, m]));
        const items = catalog.items.map((it) => {
          const m = byId.get(it.upc);
          return m
            ? { ...it, packUpc: m.scannedUpc, scannedAt: new Date(m.scannedAt).getTime() }
            : { ...it, packUpc: "", scannedAt: 0 };
        });
        const merged = {
          fileName: catalog.fileName,
          importedAt: new Date(catalog.importedAt).getTime(),
          items,
        };
        setState(merged);
        saveState(merged);

        // Best-effort: look up the latest sales transaction for each scanned
        // pack UPC. Failure is non-fatal — the cards still render without it.
        const packUpcs = Array.from(new Set(items.map((i) => i.packUpc).filter(Boolean)));
        if (packUpcs.length) {
          try {
            const map = await fetchLatestSalesByPackUpc(MAPPING_TYPE, packUpcs);
            if (cancelled) return;
            setLatestSales((prev) => {
              const next = { ...prev };
              for (const u of packUpcs) next[u] = map[u] || null;
              return next;
            });
          } catch {
            // Silently ignore — the rest of the mapper still works.
          }
        }
      } catch (err) {
        if (!cancelled) setSyncError(err?.response?.data?.message || "Could not load saved data.");
      } finally {
        if (!cancelled) setSyncing(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    const scanned = items.filter((i) => i.packUpc).length;
    return { total: items.length, scanned, unscanned: items.length - scanned };
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (filter === "scanned" && !i.packUpc) return false;
      if (filter === "unscanned" && i.packUpc) return false;
      if (!q) return true;
      return (
        i.description.toLowerCase().includes(q) ||
        i.upc.toLowerCase().includes(q) ||
        i.packUpc.toLowerCase().includes(q) ||
        i.itemNumber.toLowerCase().includes(q)
      );
    });
  }, [items, filter, search]);

  const persist = (next) => {
    setState(next);
    saveState(next);
  };

  const handleFile = (file) => {
    setImportError("");
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!rows.length) throw new Error("Sheet is empty.");
        const existingByUpc = new Map(items.map((i) => [i.upc, i]));
        const next = rows.map((r) => rowToItem(r, existingByUpc)).filter((i) => i.upc || i.description);

        persist({ fileName: file.name, importedAt: Date.now(), items: next });

        try {
          const itemsForServer = next.map(({ packUpc, scannedAt, ...rest }) => rest);
          await replaceCatalog(MAPPING_TYPE, file.name, itemsForServer);
          setSyncError("");
        } catch (err) {
          setSyncError(err?.response?.data?.message || "Saved locally, but could not sync catalog to server.");
        }
      } catch (err) {
        setImportError(err.message || "Failed to read Excel file.");
      }
    };
    reader.onerror = () => setImportError("Could not read file.");
    reader.readAsArrayBuffer(file);
  };

  const setPackUpc = async (upc, packUpc) => {
    const item = state.items.find((i) => i.upc === upc);
    const prevValue = item?.packUpc || "";
    const prevTs = item?.scannedAt || 0;

    persist({
      ...state,
      items: state.items.map((i) =>
        i.upc === upc ? { ...i, packUpc, scannedAt: packUpc ? Date.now() : 0 } : i
      ),
    });

    try {
      await upsertMapping(MAPPING_TYPE, upc, packUpc, item?.description || "");
      setSyncError("");
      if (packUpc) {
        // Mark as "loading" then fetch the latest sale for just this pack UPC.
        setLatestSales((prev) => ({ ...prev, [packUpc]: undefined }));
        fetchLatestSalesByPackUpc(MAPPING_TYPE, [packUpc])
          .then((map) => {
            setLatestSales((prev) => ({ ...prev, [packUpc]: map[packUpc] || null }));
          })
          .catch(() => {
            setLatestSales((prev) => ({ ...prev, [packUpc]: null }));
          });
      }
    } catch (err) {
      setSyncError(err?.response?.data?.message || "Could not save to server. Change reverted.");
      setState((s) => {
        const reverted = {
          ...s,
          items: s.items.map((i) =>
            i.upc === upc ? { ...i, packUpc: prevValue, scannedAt: prevTs } : i
          ),
        };
        saveState(reverted);
        return reverted;
      });
    }
  };

  const onScanned = (upc) => (text) => {
    setPackUpc(upc, text);
    setScanningUpc(null);
  };

  // Remove an item from the catalog (and its scan mapping if any). Updates
  // local state optimistically; on server failure, surfaces the error and
  // reverts.
  const deleteItem = async (upc) => {
    if (scanningUpc === upc) setScanningUpc(null);
    // Bulk-mode's effect on `items.length` will re-pick activeUpc if needed.
    const prev = state;
    persist({ ...state, items: state.items.filter((i) => i.upc !== upc) });
    try {
      await deleteItemApi(MAPPING_TYPE, upc);
      setSyncError("");
    } catch (err) {
      setSyncError(err?.response?.data?.message || "Could not delete on server. Reverted.");
      setState(prev);
      saveState(prev);
    }
  };

  // Move an item to the bottom of the catalog. Persisted to the server via
  // replaceCatalog so reloads keep the order.
  const moveToBottom = async (upc) => {
    const idx = state.items.findIndex((i) => i.upc === upc);
    if (idx < 0) return;
    const moved = state.items[idx];
    const reordered = [...state.items.slice(0, idx), ...state.items.slice(idx + 1), moved];
    const next = { ...state, items: reordered };
    persist(next);
    try {
      const itemsForServer = reordered.map(({ packUpc, scannedAt, ...rest }) => rest);
      await replaceCatalog(MAPPING_TYPE, state.fileName || "", itemsForServer);
      setSyncError("");
    } catch (err) {
      setSyncError(err?.response?.data?.message || "Reorder saved locally but did not sync.");
    }
  };

  // ── Bulk scan mode ──────────────────────────────────────────────────────
  const nextUnscannedUpc = (afterUpc = null) => {
    // Order = current filtered list; fall back to all items.
    const list = (filtered.length ? filtered : items).filter((i) => i.upc);
    if (!list.length) return null;
    const startIdx = afterUpc ? list.findIndex((i) => i.upc === afterUpc) + 1 : 0;
    for (let k = 0; k < list.length; k++) {
      const i = list[(startIdx + k) % list.length];
      if (!i.packUpc) return i.upc;
    }
    return null;
  };

  const activeItem = activeUpc ? items.find((i) => i.upc === activeUpc) : null;

  // When bulk mode turns on (or current active becomes invalid), pick a target.
  useEffect(() => {
    if (!bulkMode) return;
    if (!activeUpc || !items.some((i) => i.upc === activeUpc)) {
      setActiveUpc(nextUnscannedUpc());
    }
  }, [bulkMode, items.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the hidden input focused while bulk mode is on.
  useEffect(() => {
    if (!bulkMode) return;
    const el = bulkInputRef.current;
    if (!el) return;
    el.focus();
    const refocus = () => setTimeout(() => bulkInputRef.current?.focus(), 0);
    el.addEventListener("blur", refocus);
    return () => el.removeEventListener("blur", refocus);
  }, [bulkMode, activeUpc]);

  const submitBulkScan = async () => {
    const code = bulkBuffer.trim();
    setBulkBuffer("");
    if (!code || !activeUpc) return;
    const target = activeUpc;
    await setPackUpc(target, code);
    setBulkLastSaved({ upc: target, code, at: Date.now() });
    setActiveUpc(nextUnscannedUpc(target));
  };

  const skipActive = () => {
    if (!activeUpc) return;
    setActiveUpc(nextUnscannedUpc(activeUpc));
    setBulkBuffer("");
  };

  const exportXlsx = () => {
    const rows = items.map((i) => ({
      "UPC": i.upc,
      "Item #": i.itemNumber,
      "Description": i.description,
      "Latest Price": i.latestPrice,
      "Times Ordered": i.timesOrdered,
      "Total Qty": i.totalQty,
      "Pack UPC": i.packUpc,
      "Scanned At": i.scannedAt ? new Date(i.scannedAt).toISOString() : "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cigarette Mapping");
    const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(new Blob([out], { type: "application/octet-stream" }), "cigarette-mapping.xlsx");
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all imported items and scanned UPCs (this also clears them on the server)?")) return;
    try {
      await clearAllMappings(MAPPING_TYPE);
      persist({ fileName: "", importedAt: 0, items: [] });
      setScanningUpc(null);
      setSyncError("");
    } catch (err) {
      setSyncError(err?.response?.data?.message || "Could not clear server data.");
    }
  };

  return (
    <Container className="invoice-upload-container mt-4">
      <h2 className="mb-1">Cigarette Mapper</h2>
      <p className="text-muted">Scan the pack barcode for each product to map carton UPC → pack UPC. Saved to the server per store.</p>

      {syncing && (
        <div className="text-muted small mb-2">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading saved mappings…
        </div>
      )}
      {syncError && (
        <Alert variant="warning" className="py-2 small" dismissible onClose={() => setSyncError("")}>
          {syncError}
        </Alert>
      )}

      <Card className="p-3 mb-3">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <div>
            <Form.Label className="mb-1 small text-muted">Upload Excel</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls"
              size="sm"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              style={{ maxWidth: 380 }}
            />
            {state.fileName && (
              <div className="small text-muted mt-1">
                Loaded <strong>{state.fileName}</strong> · {counts.total} items · imported {new Date(state.importedAt).toLocaleString()}
              </div>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" size="sm" disabled={!items.length} onClick={exportXlsx}>
              Export .xlsx
            </Button>
            <Button variant="outline-danger" size="sm" disabled={!items.length} onClick={clearAll}>
              Clear all
            </Button>
          </div>
        </div>

        {importError && (
          <Alert variant="danger" className="mb-0 mt-3 py-2 small" dismissible onClose={() => setImportError("")}>
            {importError}
          </Alert>
        )}
      </Card>

      {items.length > 0 && (
        <>
          <Card className="p-3 mb-3" style={{ position: "sticky", top: 80, zIndex: 5 }}>
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
              <ButtonGroup size="sm">
                <Button variant={filter === "all" ? "primary" : "outline-primary"} onClick={() => setFilter("all")}>
                  All <Badge bg="light" text="dark" className="ms-1">{counts.total}</Badge>
                </Button>
                <Button variant={filter === "unscanned" ? "primary" : "outline-primary"} onClick={() => setFilter("unscanned")}>
                  Unscanned <Badge bg="light" text="dark" className="ms-1">{counts.unscanned}</Badge>
                </Button>
                <Button variant={filter === "scanned" ? "primary" : "outline-primary"} onClick={() => setFilter("scanned")}>
                  Scanned <Badge bg="light" text="dark" className="ms-1">{counts.scanned}</Badge>
                </Button>
              </ButtonGroup>

              <div className="d-flex gap-2 align-items-center flex-wrap">
                <Button
                  size="sm"
                  variant={bulkMode ? "success" : "outline-success"}
                  onClick={() => setBulkMode((v) => !v)}
                  title="Hands-free scanner mode (USB / Bluetooth barcode scanner)"
                >
                  {bulkMode ? "● Bulk scan ON" : "Bulk scan"}
                </Button>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Search description, UPC, item#…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ maxWidth: 320 }}
                />
              </div>
            </div>
          </Card>

          {bulkMode && (
            <Card className="p-3 mb-3 border-success" style={{ position: "sticky", top: 150, zIndex: 6, background: "#f1fff5" }}>
              {/* Hidden input that swallows USB/Bluetooth scanner output. */}
              <input
                ref={bulkInputRef}
                value={bulkBuffer}
                onChange={(e) => setBulkBuffer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitBulkScan();
                  }
                }}
                aria-label="Bulk scanner input"
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: 1,
                  height: 1,
                  pointerEvents: "none",
                }}
              />
              {activeItem ? (
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="small text-muted">Scan pack for ({counts.scanned + 1} of {counts.total}):</div>
                    <div className="fw-bold" style={{ wordBreak: "break-word" }}>{activeItem.description || "(no description)"}</div>
                    <div className="small text-muted" style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                      Carton UPC: {activeItem.upc}
                      {bulkBuffer && <> · buffer: <span className="text-dark">{bulkBuffer}</span></>}
                    </div>
                    {bulkLastSaved && (
                      <div className="small text-success">
                        Last saved: {bulkLastSaved.code} → {items.find((i) => i.upc === bulkLastSaved.upc)?.description?.slice(0, 40) || bulkLastSaved.upc}
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={skipActive}>Skip</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => setBulkMode(false)}>Stop</Button>
                  </div>
                </div>
              ) : (
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-success fw-bold">All items in the current filter are scanned. 🎉</div>
                  <Button size="sm" variant="outline-secondary" onClick={() => setBulkMode(false)}>Stop</Button>
                </div>
              )}
              <div className="small text-muted mt-2">
                Tip: keep this tab focused — your scanner types into the hidden field and Enter saves + advances.
              </div>
            </Card>
          )}

          {filtered.length === 0 ? (
            <div className="text-muted text-center py-4">No items match the current filter.</div>
          ) : (
            filtered.map((item) => (
              <SwipeableCard
                key={item.upc}
                disabled={scanningUpc === item.upc}
                onSwipeAway={() => moveToBottom(item.upc)}
              >
                <ItemCard
                  item={item}
                  latestSale={item.packUpc ? latestSales[item.packUpc] : null}
                  isScanning={scanningUpc === item.upc}
                  onStartScan={() => setScanningUpc(item.upc)}
                  onStopScan={() => setScanningUpc(null)}
                  onScanned={onScanned(item.upc)}
                  onClear={() => setPackUpc(item.upc, "")}
                  onManualEdit={(text) => setPackUpc(item.upc, text)}
                  onDelete={() => deleteItem(item.upc)}
                />
              </SwipeableCard>
            ))
          )}
        </>
      )}

      {items.length === 0 && !importError && (
        <div className="text-muted text-center py-5">
          Upload a cigarette catalog Excel to get started.
        </div>
      )}
    </Container>
  );
}

export default CigaretteMapper;
